import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGoogleMapsScript } from './useGoogleMapsScript';

export function usePlacesAutocomplete(options = {}) {
  const normalizedOptions = useMemo(
    () => ({
      language: 'en',
      ...options,
    }),
    [options],
  );
  const { ready, error } = useGoogleMapsScript();
  const serviceRef = useRef(null);
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && !serviceRef.current) {
      // Google Maps API'nin tamamen yüklendiğinden emin ol
      if (
        window.google &&
        window.google.maps &&
        window.google.maps.places &&
        window.google.maps.places.AutocompleteService
      ) {
        serviceRef.current = new window.google.maps.places.AutocompleteService();
      }
    }
  }, [ready]);

  const fetchPredictions = useCallback(
    input => {
      if (!serviceRef.current || !input) {
        setPredictions([]);
        return;
      }
      setLoading(true);
      serviceRef.current.getPlacePredictions(
        { input, ...normalizedOptions },
        (results, status) => {
        setLoading(false);
        if (status !== 'OK' || !results) {
          setPredictions([]);
          return;
        }
        setPredictions(results);
      });
    },
    [normalizedOptions],
  );

  const clearPredictions = useCallback(() => {
    setPredictions([]);
  }, []);

  const getPlaceDetails = useCallback(
    placeId =>
      new Promise((resolve, reject) => {
        if (!ready) {
          reject(new Error('Google Maps script not ready'));
          return;
        }
        const service = new window.google.maps.places.PlacesService(
          document.createElement('div'),
        );
        service.getDetails(
          {
            placeId,
            fields: ['geometry', 'formatted_address'],
            language: normalizedOptions.language || 'en',
          },
          (place, status) => {
            if (status !== 'OK' || !place) {
              reject(new Error('Unable to fetch place details'));
              return;
            }
            resolve({
              description: place.formatted_address || '',
              placeId,
              latitude: place.geometry?.location?.lat(),
              longitude: place.geometry?.location?.lng(),
            });
          },
        );
      }),
    [ready, normalizedOptions.language],
  );

  return {
    ready,
    error,
    predictions,
    loading,
    fetchPredictions,
    clearPredictions,
    getPlaceDetails,
  };
}

